import { useCallback, useEffect, useState } from 'react';
import { storeService } from '../services/storeService';
import { StoreReview } from '../models';

const PAGE_SIZE = 10;

export function useProductReviews(productId: number | undefined) {
  const [reviews, setReviews] = useState<StoreReview[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!productId) return;

    let active = true;
    setIsLoading(true);
    setError(null);
    setPageNumber(1);

    storeService
      .getProductReviews(productId, { pageNumber: 1, pageSize: PAGE_SIZE })
      .then((data) => {
        if (!active) return;
        setReviews(data.items);
        setTotalCount(data.count);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err : new Error('Erro ao carregar avaliações'));
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [productId]);

  const loadMore = useCallback(() => {
    if (!productId) return;
    const nextPage = pageNumber + 1;
    setIsLoadingMore(true);

    storeService
      .getProductReviews(productId, { pageNumber: nextPage, pageSize: PAGE_SIZE })
      .then((data) => {
        setReviews((prev) => [...prev, ...data.items]);
        setTotalCount(data.count);
        setPageNumber(nextPage);
      })
      .finally(() => setIsLoadingMore(false));
  }, [productId, pageNumber]);

  const averageRating =
    reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0;

  return {
    reviews,
    totalCount,
    averageRating,
    isLoading,
    isLoadingMore,
    error,
    isEmpty: !isLoading && totalCount === 0,
    hasMore: reviews.length < totalCount,
    loadMore,
  };
}
